import * as XLSX from 'xlsx';
import { formatRecognizedRevenueAmount } from 'utils/recognizedRevenueAmount';
import { getUnresolvedRevenueEventNotice } from 'utils/recognizedRevenueBasis';

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export const currentReportMonth = (now = new Date()) => {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Ho_Chi_Minh', year: 'numeric', month: '2-digit',
  }).formatToParts(now).map((part) => [part.type, part.value]));
  return `${parts.year}-${parts.month}`;
};

export const getMonthPeriod = (month) => {
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(month || '')) return null;
  if (month.startsWith('0000-')) return null;
  const end = new Date(`${month}-01T00:00:00Z`);
  end.setUTCMonth(end.getUTCMonth() + 1);
  end.setUTCDate(0);
  const lastDay = end.getUTCDate();
  return { date_from: `${month}-01`, date_to: `${month}-${String(lastDay).padStart(2, '0')}` };
};

export const isReportDate = (value) => {
  if (!DATE_PATTERN.test(value || '')) return false;
  if (value.startsWith('0000-')) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
};

export const getHierarchyPeriodError = (dateFrom, dateTo) => {
  if (!isReportDate(dateFrom) || !isReportDate(dateTo) || dateFrom > dateTo) {
    return 'Chọn khoảng ngày hợp lệ; ngày kết thúc phải từ ngày bắt đầu trở đi.';
  }
  if ((new Date(`${dateTo}T00:00:00Z`) - new Date(`${dateFrom}T00:00:00Z`)) / 86400000 + 1 > 366) {
    return 'Khoảng thời gian báo cáo không được vượt quá 366 ngày.';
  }
  return '';
};

export const getSalesHierarchyInitialFilters = (search = '') => {
  const params = new URLSearchParams(search);
  const defaultPeriod = getMonthPeriod(currentReportMonth());
  const dateFrom = params.get('date_from');
  const dateTo = params.get('date_to');
  const validPeriod = !getHierarchyPeriodError(dateFrom, dateTo);
  return {
    ...(validPeriod ? { date_from: dateFrom, date_to: dateTo } : defaultPeriod),
    business_id: params.get('business_id') || '',
    store_id: params.get('store_id') || '',
    view: ['daily', 'units'].includes(params.get('view')) ? params.get('view') : 'stores',
  };
};

const hasNumber = (value) => value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value));

export const formatHierarchyNumber = formatRecognizedRevenueAmount;

export const formatHierarchyDate = (value) => {
  if (!isReportDate(value)) return value || '—';
  const [year, month, day] = value.split('-');
  return `${day}/${month}/${year}`;
};

export const hierarchyColumnLabel = (level, daily) => daily || level === 'store'
  ? 'Thời gian' : level === 'branch' ? 'Cửa hàng' : 'Chi nhánh';

export const hierarchyReportTitle = (level, daily) => daily || level === 'store'
  ? 'Báo cáo bán hàng theo thời gian' : level === 'branch'
    ? 'Báo cáo bán hàng theo cửa hàng' : 'Báo cáo bán hàng theo chi nhánh';

export const isSalesHierarchyReport = (report, companyId) => Boolean(report
  && `${report.scope?.company_id}` === `${companyId}`
  && ['company', 'branch', 'store'].includes(report.level)
  && ['gross_revenue', 'return_amount', 'net_revenue'].every((field) => hasNumber(report.summary?.[field]))
  && Array.isArray(report.rows) && Array.isArray(report.daily) && Array.isArray(report.store_rows));

export const getHierarchyRows = (report, daily, view = 'units') => daily ? report?.daily || []
  : view === 'stores' ? report?.store_rows || [] : report?.rows || [];

export const buildSalesHierarchyExportSheet = (report, daily, view = 'units') => {
  const scope = report?.scope || {};
  const summary = report?.summary || {};
  const period = report?.period || {};
  const rows = getHierarchyRows(report, daily, view);
  const tableLevel = view === 'stores' && !daily ? 'branch' : report?.level;
  const moneyValues = (row) => ['gross_revenue', 'return_amount', 'net_revenue'].map((field) => hasNumber(row[field]) ? Number(row[field]) : null);
  const worksheet = XLSX.utils.aoa_to_sheet([
    [hierarchyReportTitle(tableLevel, daily).toLocaleUpperCase('vi-VN')],
    ['Tổng công ty', scope.company_name || ''],
    ['Chi nhánh', scope.business_name || 'Tất cả'],
    ['Cửa hàng', scope.store_name || 'Tất cả'],
    ['Từ ngày', formatHierarchyDate(period.date_from), 'Đến ngày', formatHierarchyDate(period.date_to)],
    ['Doanh thu thuần chưa VAT, theo ngày thực xuất kho; trả hàng theo ngày nhập trả thực tế; không gồm chuyển hàng nội bộ.'],
    [hierarchyColumnLabel(tableLevel, daily), 'Doanh thu bán hàng', 'Giảm trừ', 'Doanh thu thuần (chưa VAT)'],
    ['Tổng cộng', ...moneyValues(summary)],
    ...rows.map((row) => [row.date ? formatHierarchyDate(row.date)
      : view === 'stores' && !scope.business_id && row.business_name ? `${row.label} · ${row.business_name}` : row.label || '', ...moneyValues(row)]),
    ...(getUnresolvedRevenueEventNotice(report?.meta) ? [[getUnresolvedRevenueEventNotice(report.meta)]] : []),
  ]);
  worksheet['!cols'] = [{ wch: 48 }, { wch: 24 }, { wch: 24 }, { wch: 24 }];
  worksheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 3 } }];
  for (let row = 7; row < 8 + rows.length; row += 1) {
    for (let column = 1; column <= 3; column += 1) {
      const cell = worksheet[XLSX.utils.encode_cell({ r: row, c: column })];
      if (cell) cell.z = '#,##0.####;[Red]-#,##0.####';
    }
  }
  return worksheet;
};
